import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import MultiplicationActivityWrapper from '~/bootcamp/Activities/Multiplication/components/MultiplicationActivityWrapper'
import Times from '~/bootcamp/Activities/components/Times'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

import { floatDiff, getMultiBy, spaceEveryThousand } from './constants'
import { NumHistory } from './types'

export default function MultiBy10Deci({
  answerState,
  onAnswerStateChange,
  onInstanceChange,
  difficultyLevel,
  levelId,
  onNextQuestionClick,
  actionButtonsFC,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const [answer, setAnswer] = useState('')
  const [correctAnswer, setCorrectAnswer] = useState('')
  const [inputKey, setInputKey] = useState<string>(uuid())
  const [multiBy, setMultiBy] = useState(levelId ? getMultiBy(parseInt(levelId) - 1) : 10)
  const [taskHistory, setTaskHistory] = useState<NumHistory[]>([])
  const historyNumbers = useMemo(() => taskHistory.map(({ num }) => num), [taskHistory])
  const difficulty = floatDiff[difficultyLevel - (levelId === '4' ? 0 : 1)]
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  const { t } = useTranslation(['bootcamp', 'generics'])

  const decimalNumber = useMemo(() => {
    if (difficultyLevel && levelId) {
      return decimalGenerate(
        multiBy,
        levelId,
        difficulty.MIN_DECIMAL,
        difficulty.MAX_DECIMAL,
        difficulty.MIN_NUMBER,
        difficulty.MAX_NUMBER,
        historyNumbers,
        setMultiBy,
        setCorrectAnswer,
      )
    }
  }, [
    historyNumbers,
    difficultyLevel,
    multiBy,
    levelId,
    difficulty.MAX_DECIMAL,
    difficulty.MAX_NUMBER,
    difficulty.MIN_DECIMAL,
    difficulty.MIN_NUMBER,
  ])

  useEffect(() => {
    setAnswer('')
  }, [decimalNumber])

  const decimalSign = t('bootcamp:signs.decimal')

  useEmitInstance(
    onInstanceChange,
    decimalNumber && multiBy ? `${decimalNumber.replace('.', decimalSign)} \\cdot ${multiBy} = \\square` : null,
  )

  const handleOnCheckAnswerClick = (): void => {
    const parsedAnswer = answer?.replace(decimalSign, '.')
    const isCorrect = parseFloat(correctAnswer) === parseFloat(parsedAnswer)
    onAnswerStateChange(isCorrect)
  }

  const handleOnNextQuestionClick = (): void => {
    onAnswerStateChange(null)
    setAnswer('')

    if (decimalNumber) {
      setTaskHistory((prevState) => {
        return [{ num: parseFloat(decimalNumber) }, ...prevState].slice(0, 3)
      })
    }
    onNextQuestionClick()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (answer !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper className="pt-5 md:py-5">
      <MultiplicationActivityWrapper>
        <div>
          <span>{decimalNumber?.replace('.', decimalSign)}</span>
          <Times />
          <span>{multiBy ? spaceEveryThousand(multiBy) : ''}</span>
        </div>
        <div>
          <Equals />
          <Input
            aria-label={t('generics:ariaLabelButtons.insertProduct')}
            autoFocus={autofocus}
            isError={answerState === false}
            isWide
            key={inputKey}
            maxDigits={15}
            onChange={(value) => {
              setAnswer(value)
              onAnswerStateChange(null)
            }}
            onEnter={onEnterPress}
            ref={inputRef}
            type="text"
            value={answer}
          />
        </div>
      </MultiplicationActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== '',
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => setAnswer(''),
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

function decimalGenerate(
  multiBy: number,
  levelId: string,
  minLength: number,
  maxLength: number,
  min: number,
  max: number,
  historyNumbers: number[],
  setMultiBy: Dispatch<SetStateAction<number>>,
  setCorrectAnswer: Dispatch<SetStateAction<string>>,
): string {
  const length = Math.floor(Math.random() * (maxLength - minLength + 1) + minLength)
  const rendomNum = Math.floor(Math.random() * (max - min) + min)
  const numberOfDigits = 10 ** length
  const deciNumber =
    Math.floor(Math.random() * (rendomNum * numberOfDigits - 1 * length) + numberOfDigits) / numberOfDigits

  setMultiBy(getMultiBy(parseInt(levelId) - 1))
  if (historyNumbers.includes(deciNumber)) {
    return decimalGenerate(
      multiBy,
      levelId,
      minLength,
      maxLength,
      min,
      max,
      historyNumbers,
      setMultiBy,
      setCorrectAnswer,
    )
  }
  const finalNumber = deciNumber.toFixed(length)
  setCorrectAnswer((deciNumber * multiBy).toFixed(length))
  return finalNumber
}
